import { useEffect, useState } from "react";

import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";

import Input from "../../components/form/front/Input";
import useForm from "../../hooks/useForm";
import Button from "../../components/form/front/Button";
import FileUploadSingle from "../../components/form/FileUploadSingle";
import Textarea from "../../components/form/front/Textarea";
import {
  organizationAddEdit,
  organizationDetails,
} from "../../services/organizationService";
import Select from "../../components/form/front/Select";
import { taxonomyList } from "../../services/taxonomyService";
import { useNavigate } from "react-router-dom";

const validation = {
  // Organization Section - Required
  name: { required: true, message: "Company Name is required" },

  contactEmail: [
    {
      required: true,
      message: "Contact Email is required",
    },
    {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Enter Valid Email",
    },
  ],
};

const ManageCompany = ({ getResponse = () => { }, close = () => { }, data }) => {
  const [availableLanguages, setAvailableLanguages] = useState(["en"]);
  const [defaultLanguage, setDefaultLanguage] = useState("en");
  const [addresses, setAddresses] = useState([]);
  const [customErrors, setCustomErrors] = useState({});
  const userLangStorage = localStorage.getItem("lang") || "en";
  const navigate = useNavigate();
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
  });

  const { data: details } = useQuery({
    queryKey: ["organization-details", data?._id],
    queryFn: async () => {
      const res = await organizationDetails({ id: data?._id });
      return res?.data;
    },
    enabled: !!data?._id,
  });
  const { data: jobDepartmentData } = useQuery({
    queryKey: ["department"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "department",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });
  // Transform API response to form values
  useEffect(() => {
    if (details?._id) {
      const lang = details?.defaultLanguage || "en";

      // Extract multilingual content (if exists, otherwise use root level fields)
      const contentData = details?.content?.[lang] || {};

      // Set form fields
      setFieldsValue({
        // Basic fields
        _id: details?._id,
        type: details?.type || "",
        slug: details?.slug || "",
        status: details?.status || "inactive",
        verified: details?.verified ?? false,
        isSuspended: details?.isSuspended ?? false,
        createdAt: details?.createdAt || "",

        // Multilingual content fields
        // Try content structure first, then fall back to root level fields
        name: contentData?.title || details?.name || "",
        description: contentData?.content || details?.content || "",
        excerpt: contentData?.excerpt || details?.excerpt || "",

        // Contact info
        contactEmail: details?.contactEmail || "",
        email: details?.email || "",

        // User info - these might not exist in organization response
        firstName: details?.firstName || "",
        lastName: details?.lastName || "",
        gender: details?.gender || "",
        password: "", // Never populate password from API for security

        // Business info (flatten for form)
        registrationNumber: details?.businessInfo?.registrationNumber || "",
        taxId: details?.businessInfo?.taxId || "",
        vatNumber: details?.businessInfo?.vatNumber || "",
        employeeCount: details?.businessInfo?.employeeCount || "",
        department:
          details?.businessInfo?.department?._id ||
          details?.businessInfo?.department ||
          "",

        // Social links (flatten for form)
        // Priority: socialLinks.website > root website
        website: details?.socialLinks?.website || details?.website || "",
        linkedin: details?.socialLinks?.linkedin || "",
        twitter: details?.socialLinks?.twitter || "",
        facebook: details?.socialLinks?.facebook || "",

        // Images
        logo: details?.logo?.url || details?.logo || "",
        coverImage: details?.coverImage?.url || details?.coverImage || "",
      });

      // Set languages
      setAvailableLanguages(details?.availableLanguages || ["en"]);
      setDefaultLanguage(lang);

      // Handle addresses
      if (
        details?.address &&
        Array.isArray(details.address) &&
        details.address.length > 0
      ) {
        setAddresses(
          details.address.map((addr) => ({
            name: addr?.name || "",
            address1: addr?.address1 || "",
            address2: addr?.address2 || "",
            city: addr?.city || "",
            state: addr?.state || "",
            country: addr?.country || "",
            zipcode: addr?.zipcode || "",
          })),
        );
      } else {
        // Initialize with empty array if no addresses
        setAddresses([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);
  const resetFormState = () => {
    setFieldsValue({
      name: "",
      contactEmail: "",
      website: "",
      employeeCount: "",
      department: "",
      excerpt: "",
      description: "",
      logo: null,
      coverImage: null,
    });

    setAddresses([]);
    setAvailableLanguages(["en"]);
    setDefaultLanguage("en");
    setCustomErrors({});
  };
  // Transform form values to API payload
  const transformToApiPayload = (formValues) => {
    const formData = new FormData();

    // Add basic fields
    if (details?._id) {
      formData.append("id", details._id);
      // Make id accessible for service method check
      formData.id = details._id;
    }

    formData.append("type", "standard");

    formData.append("contactEmail", formValues.contactEmail || "");

    // Add multilingual content - ensure defaultLanguage is "en" if not set
    const contentLanguage = defaultLanguage || "en";
    formData.append(
      `content[${contentLanguage}][title]`,
      formValues.name || "",
    );
    formData.append(
      `content[${contentLanguage}][content]`,
      formValues.description || "",
    );
    formData.append(
      `content[${contentLanguage}][excerpt]`,
      formValues.excerpt || "",
    );

    // Add languages - ensure defaultLanguage defaults to "en"
    formData.append("defaultLanguage", contentLanguage);
    const langs = availableLanguages.length > 0 ? availableLanguages : ["en"];
    langs.forEach((lang) => {
      formData.append("availableLanguages[]", lang);
    });

    // Add businessInfo
    // formData.append(
    //   "businessInfo[registrationNumber]",
    //   formValues.registrationNumber || "",
    // );
    // formData.append("businessInfo[taxId]", formValues.taxId || "");
    // formData.append("businessInfo[vatNumber]", formValues.vatNumber || "");
    formData.append(
      "businessInfo[employeeCount]",
      formValues.employeeCount || "",
    );
    formData.append("businessInfo[department]", formValues.department || "");
    // Add socialLinks
    formData.append("socialLinks[website]", formValues.website || "");

    // Add addresses
    addresses.forEach((addr, index) => {
      formData.append(`address[${index}][name]`, addr.name || "");
      formData.append(`address[${index}][address1]`, addr.address1 || "");
      formData.append(`address[${index}][address2]`, addr.address2 || "");
      formData.append(`address[${index}][city]`, addr.city || "");
      formData.append(`address[${index}][state]`, addr.state || "");
      formData.append(`address[${index}][country]`, addr.country || "");
      formData.append(`address[${index}][zipcode]`, addr.zipcode || "");
    });

    // Add files
    if (formValues.logo && typeof formValues.logo === "object") {
      formData.append("logo", formValues.logo);
    }
    if (formValues.coverImage && typeof formValues.coverImage === "object") {
      formData.append("coverImage", formValues.coverImage);
    }

    return formData;
  };

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const payload = transformToApiPayload(val);
      return await organizationAddEdit(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        getResponse(res);
        if (!data?._id) {
          resetFormState();

          setTimeout(() => {
            navigate("/");
          }, 800);
        }
        return res;
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
      toast.error("Failed to save company");
    },
  });

  const handleAddressChange = (e, index) => {
    const { name, value } = e.target;
    const field = name.split("-")[0];
    setAddresses((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (field === "name" && customErrors[`address_${index}_name`]) {
      const newErrors = { ...customErrors };
      delete newErrors[`address_${index}_name`];
      if (value.trim() !== "") {
        delete newErrors.addresses;
      }
      setCustomErrors(newErrors);
    }
  };

  const languages = [
    { name: "English", value: "en", code: "GB" },
    { name: "Spanish", value: "es", code: "ES" },
    { name: "French", value: "fr", code: "FR" },
    { name: "German", value: "de", code: "DE" },
  ];

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      {/* ===== Main Card Wrapper ===== */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 space-y-10">
        {/* ================= COMPANY INFORMATION ================= */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-3">
            Company Information
          </h2>

          <div className="grid sm:grid-cols-12 gap-x-6 gap-y-6">
            <div className="sm:col-span-6">
              <Input
                label="Company Name"
                inputPlaceholder="Enter Company Name"
                inputName="name"
                value={values?.name || ""}
                onChange={handleChange}
                required
                {...formValidate(errors, "name")}
                inputClasses={"border-0 shadow-[0_0_4px_rgba(0,0,0,0.12)] placeholder:text-sm"}

              />
            </div>

            <div className="sm:col-span-6">
              <Input
                label="Contact Email"
                inputPlaceholder="Enter Contact Email"
                inputName="contactEmail"
                inputType="email"
                value={values?.contactEmail || ""}
                onChange={handleChange}
                required
                {...formValidate(errors, "contactEmail")}
                inputClasses={"border-0 shadow-[0_0_4px_rgba(0,0,0,0.12)] placeholder:text-sm"}
              />
            </div>

            <div className="sm:col-span-6">
              <Input
                label="Website"
                inputPlaceholder="Enter Website URL"
                inputName="website"
                value={values?.website || ""}
                onChange={handleChange}
                inputClasses={"border-0 shadow-[0_0_4px_rgba(0,0,0,0.12)] placeholder:text-sm"}
              />
            </div>

            <div className="sm:col-span-6">
              <Select
                label="Organization Size"
                placeholder="Select Size"
                dropdownButtonClass="h-10 border-0 shadow-[0_0_4px_rgba(0,0,0,0.12)]"
                labelClasses={"text-gray-500"}
                selectedValue={values?.employeeCount}
                dropdownData={[
                  { name: "1-10 employees", value: "1-10" },
                  { name: "11-50 employees", value: "11-50" },
                  { name: "51-200 employees", value: "51-200" },
                  { name: "201-500 employees", value: "201-500" },
                  { name: "501-1000 employees", value: "501-1000" },
                  { name: "1000+ employees", value: "1000+" },
                ]}
                onChange={(val) =>
                  handleChange({
                    target: { name: "employeeCount", value: val },
                  })
                }

              />
            </div>

            <div className="sm:col-span-6">
              <Select
                label={"Industry"}
                placeholder={"Choose departments"}
                dropdownButtonClass="h-10 border-0 shadow-[0_0_4px_rgba(0,0,0,0.12)]"
                labelClasses={"text-gray-500"}
                onChange={(e) => {
                  handleChange({
                    target: { name: "department", value: e },
                  });
                  if (customErrors.department) {
                    setCustomErrors({ ...customErrors, department: "" });
                  }
                }}
                selectedValue={values?.department}
                dropdownData={jobDepartmentData?.data?.map((item) => {
                  return {
                    name:
                      item?.content?.[userLangStorage]?.title ||
                      item?.content?.en?.title,
                    value: item?._id,
                  };
                })}
              />
            </div>

            <div className="sm:col-span-12">
              <Textarea
                label="Tagline"
                inputPlaceholder="Enter Short Description"
                inputName="excerpt"
                inputValue={values?.excerpt || ""}
                onChange={handleChange}
                inputClasses={"placeholder:text-sm border-0 shadow-[0_0_4px_rgba(0,0,0,0.12)]"}
              />
            </div>

            <div className="sm:col-span-12">
              <Textarea
                label="Description"
                inputPlaceholder="Enter Description"
                inputName="description"
                inputValue={values?.description || ""}
                onChange={handleChange}
                inputClasses={"placeholder:text-sm border-0 shadow-[0_0_4px_rgba(0,0,0,0.12)]"}
              />
            </div>
          </div>
        </div>

        {/* ================= IMAGES ================= */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-3">
            Branding
          </h2>

          <div className="grid sm:grid-cols-2 gap-8">
            <FileUploadSingle
              label="Upload Logo"
              value={values?.logo}
              onChange={(file) =>
                handleChange({ target: { name: "logo", value: file } })
              }
              
            />

            <FileUploadSingle
              label="Upload Cover Image"
              value={values?.coverImage}
              onChange={(file) =>
                handleChange({
                  target: { name: "coverImage", value: file },
                })
              }
            />
          </div>
        </div>

        {/* ================= ADDRESSES ================= */}
        <div>
          <div className="flex justify-between items-center mb-6 border-b pb-3">
            <h2 className="text-xl font-semibold text-gray-800">
              Company Addresses
            </h2>

            <Button
              buttonLabel="+ Add Address"
              buttonFunction={() =>
                setAddresses([
                  ...addresses,
                  {
                    name: "",
                    address1: "",
                    address2: "",
                    city: "",
                    state: "",
                    country: "",
                    zipcode: "",
                  },
                ])
              }
              buttonClasses="!bg-palatte text-white px-4 py-2 rounded-lg"
            />
          </div>

          <div className="space-y-6">
            {addresses.map((addr, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl p-6 bg-gray-50"
              >
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-700">
                    Address {index + 1}
                  </h4>

                  <button
                    type="button"
                    onClick={() =>
                      setAddresses((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                    className="text-red-500 text-sm hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <Input
                    label="Address Name"
                    inputName="name"
                    value={addr.name}
                    onChange={(e) => handleAddressChange(e, index)}
                  />

                  <Input
                    label="City"
                    inputName="city"
                    value={addr.city}
                    onChange={(e) => handleAddressChange(e, index)}
                  />

                  <Input
                    label="State"
                    inputName="state"
                    value={addr.state}
                    onChange={(e) => handleAddressChange(e, index)}
                  />

                  <Input
                    label="Country"
                    inputName="country"
                    value={addr.country}
                    onChange={(e) => handleAddressChange(e, index)}
                  />

                  <Input
                    label="Zipcode"
                    inputType="number"
                    inputName="zipcode"
                    value={addr.zipcode}
                    onChange={(e) => handleAddressChange(e, index)}
                  />

                  <Input
                    label="Address Line 1"
                    inputName="address1"
                    value={addr.address1}
                    onChange={(e) => handleAddressChange(e, index)}
                  />

                  <Input
                    label="Address Line 2"
                    inputName="address2"
                    value={addr.address2}
                    onChange={(e) => handleAddressChange(e, index)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ================= SUBMIT ================= */}
        <div className="pt-6 border-t flex justify-end">
          <Button
            buttonType="submit"
            buttonClasses="min-w-40"
            buttonLabel={data?._id ? "Update Company" : "Save Company"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageCompany;
